import { useCallback, useEffect, useRef, useState } from 'react'

type UseTrackOnceInViewParams = {
  enabled?: boolean
  onEnterView: () => void
  trackKey?: string
  threshold?: number | number[]
  rootMargin?: string
}

export default function useTrackOnceInView({
  enabled = true,
  onEnterView,
  trackKey,
  threshold = 0,
  rootMargin = '0px',
}: UseTrackOnceInViewParams) {
  const [targetNode, setTargetNode] = useState<Element | null>(null)
  const hasTrackedRef = useRef(false)
  const onEnterViewRef = useRef(onEnterView)

  const targetRef = useCallback((node: Element | null) => {
    setTargetNode(node)
  }, [])

  useEffect(() => {
    onEnterViewRef.current = onEnterView
  }, [onEnterView])

  useEffect(() => {
    hasTrackedRef.current = false
  }, [trackKey])

  useEffect(() => {
    if (!enabled || !targetNode || hasTrackedRef.current) return

    if (typeof IntersectionObserver === 'undefined') {
      hasTrackedRef.current = true
      onEnterViewRef.current()
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || hasTrackedRef.current) return

        hasTrackedRef.current = true
        onEnterViewRef.current()
        observer.disconnect()
      },
      {
        threshold,
        rootMargin,
      }
    )

    observer.observe(targetNode)

    return () => {
      observer.disconnect()
    }
  }, [enabled, rootMargin, targetNode, threshold, trackKey])

  return targetRef
}
